import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { trackButtonClick } from '../../utils/analytics'
import { USFlag, MexicoFlag, CanadaFlag, BrazilFlag, RussiaFlag, ChinaFlag, GermanyFlag, NetherlandsFlag } from '../flags/FlagIcons'

/**
 * LanguageSelector - Glassmorphic language selection interface
 * 
 * Features:
 * - Modern glassmorphic design matching app aesthetic
 * - Grid layout with flag cards
 * - Active language indicator with visual feedback
 * - Four language options: English (US), Spanish (Mexico), French (Canada), Portuguese (Brazil)
 * - Smooth hover and selection animations
 * 
 * @idempotent - Pure presentation component
 * @atomic - Single purpose: language selection
 * @performance - Optimized with memo, useCallback, and useMemo
 */
const LanguageSelector = memo(function LanguageSelector({
  onLanguageChange,
  compact = false
}) {
  const { t, i18n } = useTranslation()

  const languages = useMemo(() => [
    { code: 'en', flag: USFlag, name: t('settings.languages.english'), region: t('settings.languages.unitedStates') },
    { code: 'es-MX', flag: MexicoFlag, name: t('settings.languages.spanish'), region: t('settings.languages.mexico'), altCode: 'es' },
    { code: 'fr-CA', flag: CanadaFlag, name: t('settings.languages.french'), region: t('settings.languages.canada'), altCode: 'fr' },
    { code: 'pt-BR', flag: BrazilFlag, name: t('settings.languages.portuguese'), region: t('settings.languages.brazil'), altCode: 'pt' },
    { code: 'ru', flag: RussiaFlag, name: t('settings.languages.russian'), region: t('settings.languages.russia') },
    { code: 'zh-CN', flag: ChinaFlag, name: t('settings.languages.chinese'), region: t('settings.languages.china'), altCode: 'zh' },
    { code: 'de', flag: GermanyFlag, name: t('settings.languages.german'), region: t('settings.languages.germany') },
    { code: 'nl', flag: NetherlandsFlag, name: t('settings.languages.dutch'), region: t('settings.languages.netherlands') }
  ], [t])

  const isActive = useCallback((lang) => {
    return i18n.language === lang.code || i18n.language === lang.altCode
  }, [i18n.language])

  return (
    <div className="space-y-2">
      {/* Language Grid */}
      <div className={`grid gap-2 ${compact ? 'grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {languages.map((lang) => {
          const active = isActive(lang)
          return (
            <button
              key={lang.code}
              onClick={() => {
                trackButtonClick('select_language', 'header', { 
                  language: lang.code, 
                  from_language: i18n.language 
                })
                onLanguageChange && onLanguageChange(lang.code)
              }}
              className={`relative p-2 rounded-lg transition-all duration-200 backdrop-blur-sm group ${
                active
                  ? 'bg-primary/15 border-2 border-primary shadow-md scale-[1.02]'
                  : 'bg-white/80 border border-gray-200/50 hover:bg-white/90 hover:border-primary/30 hover:shadow-sm'
              }`}
            >
              {/* Active Indicator */}
              {active && (
                <div className="absolute top-1 right-1">
                  <CheckCircleIcon className={`text-primary ${compact ? 'w-3 h-3' : 'w-4 h-4'}`} />
                </div>
              )}
              
              {/* Flag */}
              <div className="flex justify-center mb-1">
                <div className="transform group-hover:scale-110 transition-transform duration-200">
                  <lang.flag className={`rounded shadow-sm ${compact ? 'w-8 h-5' : 'w-10 h-7'}`} />
                </div>
              </div>
              
              {/* Language Info */}
              <div className="text-center overflow-hidden">
                <p className={`font-semibold leading-tight mb-0.5 truncate ${compact ? 'text-[10px]' : 'text-xs'} ${
                  active ? 'text-primary' : 'text-gray-900'
                }`}>
                  {lang.name}
                </p>
                {!compact && (
                  <p className="text-[10px] text-gray-500 truncate">
                    {lang.region}
                  </p>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Help Text — hidden in compact mode */}
      {!compact && (
        <div className="bg-blue-50/80 backdrop-blur-sm rounded-xl p-3 border border-blue-100/50">
          <p className="text-xs text-gray-700 text-center leading-relaxed">
            {t('settings.languageChangeInfo', 'Your language preference is saved automatically and will persist across sessions.')}
          </p>
        </div>
      )}
    </div>
  )
})

export default LanguageSelector
